import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Spinner,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useVolunteerStudyICF } from "modules/Volunteer/AssigningStudyModule/StudyICF/api/useVolunteerStudyICF";
import {
  Checklist_Type_OPTIONS,
  loadCodelistsDetailId,
  PrimaryLanguage_OPTIONS,
} from "api/modules/common.option";
import { getLabelFromValue } from "api/modules/common.option";
import styles from "../css/studyicf.module.scss";
import { formatDate, showToastMessage } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import StudyICFAttachmentModal, {
  ICFRecord,
} from "Components/Study/StudyDetails/StudyICFAttachmentModal";
import StudyICFSubmissionModal from "Components/Study/StudyDetails/StudyICFSubmissionModal";
import StudyICFRemarksModal from "Components/Study/StudyDetails/StudyICFRemarksModal";
import FilePreviewModal from "../../../../Study/Attachments/components/FilePreviewModal";
import SimpleBar from "simplebar-react"; // Import SimpleBar
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { CheckListQuestion } from "../../../../Library/Checklist/helpers/checklist.types";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import {
  QuestionSkeleton,
  SwitchField,
  YES_NO_OPTIONS,
} from "../helper/studyicf.constant";
import { STUDY_PATHS } from "Routes/paths";
import { BreadcrumbContext } from "Layouts/Header";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { usePermission } from "context/PermissionContext";
import TruncatedText from "Components/Common/TruncatedText";


/* -------------------------------
   Types
--------------------------------- */

type QuestionnaireValues = Record<string, string>; // Dynamic keys based on masterchecklistid

/* -------------------------------
   Component
--------------------------------- */

export interface StudyICFProps {
  readonly?: boolean;
}

const StudyICF: React.FC<StudyICFProps> = ({ readonly }) => {
  const { hasPermission } = usePermission()
  const isEditable = !readonly && hasPermission(PERMISSION_MODULES.STUDY_SCREENING, PERMISSION_ACTIONS.ICF_ADD);
  const volunteerid = Number(sessionStorage.getItem("activevolunteerid"));
  const studyid = Number(sessionStorage.getItem("activestudyid"));

  /* -------------------------------
     DOCUMENT LOGIC (from old StudyICF)
--------------------------------- */
  const { SelectAllQuery, InsertMutation } = useVolunteerStudyICF({
    volunteerid,
    studyid,
  });

  // Fetch Master ICF Questions
  const { SelectAllQuery: MasterICFQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: "ICF",
  });

  const [codelistsLoaded, setCodelistsLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [tempICFRecord, setTempICFRecord] = useState<ICFRecord | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [viewFileUrl, setViewFileUrl] = useState<string | null>(null);
  const [viewFile, setViewFile] = useState<File | null>(null);
  const [isPreviewAudit, setIsPreviewAudit] = useState(false);

  // Audit logic
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const onAuditClick = () => {
    const payload = {
      modulename: "Study ICF",
      studyid: studyid,
      refid: volunteerid,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Volunteer_ICF_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setIsPreviewAudit(true);
          setViewFile(pdfFile);
          setViewFileUrl(null);
          setPreviewModalOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Study Screening", link: STUDY_PATHS.SCREENING_LIST },
      { text: "Study ICF" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      await loadCodelistsDetailId(["Checklist_Type_OPTIONS", "PrimaryLanguage_OPTIONS"]);
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  const icfQuestions: CheckListQuestion[] = useMemo(() => {
    return MasterICFQuery?.data?.data ?? [];
  }, [MasterICFQuery?.data?.data]);

  const icfData = SelectAllQuery?.data?.data?.[0];
  // Confirmation Modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<QuestionnaireValues | null>(null);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const toggleConfirmModal = () => {
    setConfirmModalOpen(!confirmModalOpen);
  };

  // Remarks Modal
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);

  const toggleRemarksModal = () => {
    setRemarksModalOpen(!remarksModalOpen);
  };

  const handleSubmissionConfirm = () => {
    setConfirmModalOpen(false);
    setRemarksModalOpen(true);
  };

  const handleSaveICFManual = (record: ICFRecord) => setTempICFRecord(record);

  const handleView = () => {
    setIsPreviewAudit(false);
    if (tempICFRecord) {
      setViewFile(tempICFRecord.file || null);
      setViewFileUrl(null);
      setPreviewModalOpen(true);
      return;
    }
    const API_FILE_BASE_URL =  sessionStorage.getItem("filebaseUrl") || process.env.REACT_APP_FILE_BASE_URL
    const baseUrl = API_FILE_BASE_URL;

    const path = icfData?.filepath;
    if (path) {
      setViewFile(null);
      setViewFileUrl(`${baseUrl}${path}`);
      setPreviewModalOpen(true);
    }
  };

  const handleDeleteDoc = () => {
    // This logic is only available for local temporary uploads (tempICFRecord)
    if (tempICFRecord) {
      setTempICFRecord(null);
      // showToastMessage("success", "Temporary document removed");
    }
  };

  /* -------------------------------
     QUESTIONNAIRE LOGIC (from old StudyICFOneToOne)
  --------------------------------- */
  const {
    control,
    handleSubmit,
    watch,
    reset, // Added reset
    formState: { errors },
  } = useForm<QuestionnaireValues>({
    defaultValues: {},
  });

  // Codelists load is now handled globally above to ensure icfQuestions filters properly

  // Populate dynamic default values once questions are loaded
  useEffect(() => {
    if (icfQuestions.length > 0) {
      const defaults: QuestionnaireValues = {};
      icfQuestions.forEach((q) => {
        // Try to find answer in icfData checklist
        const savedAns = icfData?.checklist?.find(
          (item: any) => item.questionid === q.masterchecklistid,
        );
        defaults[String(q.masterchecklistid)] =
          savedAns && savedAns.answeroptionid !== null && savedAns.answeroptionid !== undefined
            ? String(savedAns.answeroptionid)
            : "";
      });
      reset(defaults);
    }
  }, [icfQuestions, icfData, reset]);

  const values = watch();

  const isSubmittable = useMemo(() => {
    const allRequiredAnswered = icfQuestions.every((q) => {
      if (!q.isrequired) return true;
      const val = values[String(q.masterchecklistid)];
      return val !== "" && val !== null && val !== undefined;
    });

    const hasValidDoc =
      !!tempICFRecord &&
      !!tempICFRecord.version &&
      !!tempICFRecord.language &&
      !!tempICFRecord.signeddate;

    return allRequiredAnswered && hasValidDoc;
  }, [tempICFRecord, icfData, icfQuestions, values]);

  /* -------------------------------
     SUBMISSION LOGIC
  --------------------------------- */

  const onQuestionnaireSubmit = (data: QuestionnaireValues) => {
    setPendingFormData(data);
    setConfirmModalOpen(true);
  };

  const handleFinalSave = (remarks: string) => {
    if (!pendingFormData) return;

    const data = pendingFormData;
    const formData = new FormData();
    formData.append("volunteerid", String(volunteerid));
    formData.append("studyid", String(studyid));
    formData.append("remarks", remarks);

    if (tempICFRecord) {
      formData.append("icfversion", tempICFRecord.version || "");
      formData.append("language", String(tempICFRecord.language || ""));
      // formData.append("signeddate", tempICFRecord.signeddate || "");
      formData.append(
        "signeddate",
        tempICFRecord.signeddate
          ? new Date(tempICFRecord.signeddate).toISOString()
          : "",
      );
      if (tempICFRecord.file) {
        formData.append("files", tempICFRecord.file);
      }
    }

    // Combine questionnaire results into JSON format
    const checklistData = icfQuestions
      .filter(
        (q) =>
          data[q.masterchecklistid] !== "" &&
          data[q.masterchecklistid] !== null &&
          data[q.masterchecklistid] !== undefined,
      ) // Filter out unanswered questions
      .map((q) => ({
        questionid: q.masterchecklistid,
        answeroptionid: Number(data[q.masterchecklistid]),
      }));

    formData.append("question_answers", JSON.stringify(checklistData));

    InsertMutation.mutate(formData, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message);
        queryClient.invalidateQueries({
          queryKey: ["volunteer-study-icf-selectAll"],
        });
        queryClient.invalidateQueries({ queryKey: ["icf-master-selectAll"] });
        setTempICFRecord(null);
        setPendingFormData(null);
        setConfirmModalOpen(false);
        setRemarksModalOpen(false);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message ?? err ?? "Something went wrong");
      },
    });
  };

  if (false) {
    // Global loader removed as per request
  }

  const displayQuestions = useMemo(() => {
    if (!icfData) return icfQuestions;
    return icfQuestions.filter((q) =>
      icfData.checklist?.some((item: any) => item.questionid === q.masterchecklistid),
    );
  }, [icfQuestions, icfData]);

  return (
    <div className={styles.icf_container}>
      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
        <div>
          <h4 className="fw-bold text-primary mb-1">ICF Evaluation & Upload</h4>
          <p className="text-muted mb-0 fs-13 fw-normal">
            Evaluate the participant's understanding across mandatory
            comprehension points and upload the signed Informed Consent Form.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {!readonly && (
            <CommonButton
              module={PERMISSION_MODULES.STUDY_SCREENING}
              action={PERMISSION_ACTIONS.ICF_AUDIT}
              color="primary"
              size="sm"
              onClick={onAuditClick}
              disabled={AuditMutation.isPending}
              outline
              className="fw-600 "
              style={{ height: "35px" }}
            >
              {AuditMutation.isPending ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="ri-history-line me-1"></i>
              )}
              Audit
            </CommonButton>
          )}
          {!icfData && (
            <CommonButton
              module={PERMISSION_MODULES.STUDY_SCREENING}
              action={PERMISSION_ACTIONS.ICF_ADD}
              color="primary"
              outline
              className="d-flex align-items-center gap-1 fw-bold fs-13"
              onClick={handleSubmit(onQuestionnaireSubmit)}
              disabled={!isSubmittable || !isEditable || displayQuestions?.length === 0}
              style={{ height: "35px" }}
            >
              <i className="ri-save-3-line fs-15"></i> Save ICF Evaluation
            </CommonButton>
          )}
        </div>
      </div>

      <Row className="g-4">
        {/* LEFT COLUMN: ICF ONE-TO-ONE (QUESTIONNAIRE) */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100 rounded-4">
            <CardBody className="p-3 pb-0">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="avatar-xs">
                  <span className="avatar-title bg-info-subtle text-info rounded-circle fs-5">
                    <i className="ri-user-follow-line"></i>
                  </span>
                </div>
                <h5 className="mb-0 fw-bold fs-15 text-dark">
                  ICF One-to-One Understanding
                </h5>
              </div>

              <SimpleBar style={{ height: "310px" }} className="pe-2">
                <div className="d-flex flex-column gap-2">
                  {MasterICFQuery.isLoading || SelectAllQuery.isFetching ? (
                    [1, 2, 3, 4, 5].map((n) => <QuestionSkeleton key={n} />)
                  ) : displayQuestions.length > 0 ? (
                    displayQuestions.map((q) => {
                      return (
                        <SwitchField
                          key={q.masterchecklistid}
                          name={String(q.masterchecklistid)}
                          label={q.question}
                          icon="ri-question-line"
                          options={YES_NO_OPTIONS}
                          control={control}
                          required={q.isrequired === true}
                          disabled={!!icfData || !isEditable}
                        />
                      );
                    })
                  ) : (
                    <div className="text-center p-5 bg-light rounded-4 border border-light border-dashed">
                      <div className="avatar-md mx-auto mb-3">
                        <div className="avatar-title bg-white text-muted rounded-circle fs-1 border">
                          <i className="ri-questionnaire-line"></i>
                        </div>
                      </div>
                      <h6 className="text-muted fw-bold">
                        No evaluation questionnaire has been assigned to this ICF.<br /> Please contact the administrator for further information.
                      </h6>
                    </div>
                  )}
                </div>
              </SimpleBar>
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT COLUMN: DOCUMENT MANAGEMENT (FILE UPLOAD) */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100 rounded-4">
            <CardBody className="p-4 d-flex flex-column">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-4">
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar-xs">
                    <span className="avatar-title bg-info-subtle text-info rounded-circle fs-5">
                      <i className="ri-file-copy-2-line"></i>
                    </span>
                  </div>
                  <h5 className="mb-0 fw-bold fs-15 text-dark">
                    ICF Document Management
                  </h5>
                </div>

                {/* ACTIONS */}
                {(tempICFRecord || icfData) && (
                  <div className="d-flex gap-1">
                    {(tempICFRecord?.file || icfData?.filepath) && (
                      <Button
                        color="link"
                        className="p-1 px-2 text-info text-decoration-none shadow-none"
                        onClick={handleView}
                        title="View Document"
                      >
                        <i className="ri-eye-line fs-17"></i>
                      </Button>
                    )}

                    {/* Only show Edit/Delete for local temporary uploads */}
                    {tempICFRecord && (
                      <>
                        <Button
                          color="link"
                          className="p-1 px-2 text-primary text-decoration-none shadow-none"
                          onClick={toggleModal}
                          title="Edit/Replace"
                          disabled={!isEditable}
                        >
                          <i className="ri-edit-line fs-17"></i>
                        </Button>
                        <Button
                          color="link"
                          className="p-1 px-2 text-danger text-decoration-none shadow-none"
                          onClick={handleDeleteDoc}
                          title="Remove Document"
                          disabled={!isEditable}
                        >
                          <i className="ri-delete-bin-line fs-17"></i>
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div
                className={`flex-grow-1 ${styles.upload_area} ${icfData?.filepath ? styles.uploaded : ""}`}
              >
                {SelectAllQuery.isFetching ? (
                  <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center p-4 placeholder-glow">
                    <div
                      className="avatar-lg mb-4 placeholder rounded-circle"
                      style={{
                        width: "85px",
                        height: "85px",
                        backgroundColor: "#e9ecef",
                      }}
                    ></div>
                    <div
                      className="placeholder col-8 mb-2 rounded"
                      style={{ height: "20px", backgroundColor: "#e9ecef" }}
                    ></div>
                    <div
                      className="placeholder col-5 rounded"
                      style={{ height: "16px", backgroundColor: "#e9ecef" }}
                    ></div>
                  </div>
                ) : tempICFRecord || icfData ? (
                  <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center p-3">
                    {/* Document Info Card - Now fills more space */}
                    <div className="w-100 flex-grow-1 d-flex flex-column justify-content-center align-items-center bg-light-subtle rounded-4 border border-light p-4 shadow-sm">
                      <div className="avatar-lg mb-2">
                        <div className="avatar-title bg-white shadow-sm rounded-circle fs-1 border border-light h-70 w-70">
                          <i
                            className="ri-file-text-line"
                            style={{ fontSize: "3rem", color: "#344054" }}
                          ></i>
                        </div>
                      </div>
                      {(tempICFRecord?.file || icfData?.filepath) && (
                        <h5 className="fw-bold text-dark mb-2 text-center w-100 px-3">
                          <TruncatedText
                            text={
                              (tempICFRecord
                                ? tempICFRecord.filename
                                : (icfData
                                  ? `${icfData.filename}.${icfData.fileextension}`
                                  : "")) || "ICF_Document.pdf"
                            }
                          />
                        </h5>
                      )}
                      <div className="d-flex mt-2 gap-2">
                        <Badge
                          color="light"
                          className="text-dark border fs-12 fw-semibold px-3 py-1 rounded-pill"
                        >
                          {tempICFRecord
                            ? tempICFRecord.version
                            : icfData.icfversion || "1.0"}
                        </Badge>
                        <Badge
                          color="light"
                          className="text-dark border fs-12 fw-semibold px-3 py-1 rounded-pill"
                        >
                          {tempICFRecord
                            ? isNaN(Number(tempICFRecord.language))
                              ? (tempICFRecord.language || "English")
                              : getLabelFromValue(PrimaryLanguage_OPTIONS, tempICFRecord.language, "English")
                            : isNaN(Number(icfData?.language))
                              ? (icfData?.language || "English")
                              : getLabelFromValue(PrimaryLanguage_OPTIONS, icfData?.language, "English")}
                        </Badge>
                      </div>
                      {(tempICFRecord?.signeddate || icfData.signeddate) && (
                        <div className="text-muted fs-12 mt-3 bg-light px-2 py-1 rounded d-flex align-items-center gap-1 border border-light">
                          <i className="ri-calendar-event-line fs-14"></i>
                          <span className="fw-medium">
                            Signed:{" "}
                            {formatDate(
                              tempICFRecord
                                ? tempICFRecord.signeddate
                                : icfData.signeddate,
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="text-center"
                    onClick={isEditable ? toggleModal : undefined}
                    style={{ cursor: isEditable ? "pointer" : "default" }}
                  >
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-light text-muted rounded-circle fs-1 shadow-sm">
                        <i className="ri-file-upload-line"></i>
                      </div>
                    </div>
                    <h6 className="fw-bold text-dark mb-1">No ICF Uploaded</h6>
                    <p className="text-muted small mb-3 px-4">
                      Please strictly upload the Informed Consent Form for this
                      volunteer.
                    </p>
                    <Button
                      color="primary"
                      className="rounded-pill px-4 btn-sm shadow fw-bold"
                      disabled={!isEditable}
                    >
                      <i className="ri-add-line me-1"></i> Upload ICF Document
                    </Button>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* MODALS */}

      <StudyICFAttachmentModal
        isOpen={modalOpen}
        toggle={toggleModal}
        onSaveManual={handleSaveICFManual}
        volunteerid={volunteerid}
        studyid={studyid}
        initialData={tempICFRecord}
      />

      <FilePreviewModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileUrl={viewFileUrl}
        file={viewFile}
        size={isPreviewAudit ? "xl" : undefined}
      />

      <StudyICFSubmissionModal
        isOpen={confirmModalOpen}
        toggle={toggleConfirmModal}
        tempICFRecord={tempICFRecord}
        icfQuestions={icfQuestions}
        pendingFormData={pendingFormData}
        onConfirm={handleSubmissionConfirm}
        isloading={InsertMutation.isPending}
      />

      <StudyICFRemarksModal
        isOpen={remarksModalOpen}
        toggle={toggleRemarksModal}
        onConfirm={handleFinalSave}
        isloading={InsertMutation.isPending}
      />
    </div>
  );
};

export default StudyICF;
